// Local persistence of per-screen player tokens.
//
// We extract the token from the URL on first load and stash it in
// localStorage so a subsequent reload of `/play/:screenId` (without any token
// in the URL) still authenticates as the same player. This lets a kiosk be
// "provisioned once" by pasting a fragment URL and then survive any number of
// reloads, including ones triggered by the device/browser/OS.
//
// We intentionally use localStorage (not IndexedDB) here:
//   * the player engine boots synchronously off this value, and
//   * the storage cost is one short string per screen.
//
// For the same reason, we never persist the token in any URL form — the
// player URL is scrubbed via history.replaceState the moment we read it.

const KEY_PREFIX = 'signage:player-token:';
const LAST_PAIRED_KEY = 'signage:last-paired-screen';

function key(screenId: string): string {
  return `${KEY_PREFIX}${screenId}`;
}

export function readStoredToken(screenId: string): string | null {
  if (!screenId) return null;
  try {
    const v = localStorage.getItem(key(screenId));
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredToken(screenId: string, token: string): void {
  if (!screenId || !token) return;
  try {
    localStorage.setItem(key(screenId), token);
    // Remember which screen was last paired so /provision can resume the
    // player on cold boot without forcing a fresh code (Fire TV in
    // particular relaunches the WebView at /provision on every restart).
    localStorage.setItem(LAST_PAIRED_KEY, screenId);
  } catch {
    // Private mode / storage quota exhausted: the player still works for
    // this session, just won't survive a reload without re-pasting the URL.
  }
}

export function clearStoredToken(screenId: string): void {
  if (!screenId) return;
  try {
    localStorage.removeItem(key(screenId));
    if (localStorage.getItem(LAST_PAIRED_KEY) === screenId) {
      localStorage.removeItem(LAST_PAIRED_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Locate any persisted player token from a previous pairing. Used by the
 * /provision boot URL to recognise an already-paired device and skip
 * straight to the player instead of generating a brand-new code.
 *
 * Resolution order:
 *   1. The screen pointed at by `signage:last-paired-screen` (if its
 *      token is still present)
 *   2. The first `signage:player-token:*` entry found in localStorage
 *
 * Returns null when no usable token is stored.
 */
export function findStoredPlayerToken(): { screenId: string; token: string } | null {
  try {
    const lastId = localStorage.getItem(LAST_PAIRED_KEY);
    if (lastId) {
      const tok = readStoredToken(lastId);
      if (tok) return { screenId: lastId, token: tok };
    }
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(KEY_PREFIX)) continue;
      const v = localStorage.getItem(k);
      if (v && v.length > 0) {
        return { screenId: k.slice(KEY_PREFIX.length), token: v };
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Parse a player token out of a URL. Tries (in order):
 *   1. URL fragment    `#t=...`  or  `#token=...`
 *   2. Query string    `?t=...`  or  `?token=...`
 *
 * The fragment is preferred because it never reaches the server, never
 * appears in same-origin Referer headers, and survives only in the tab's
 * history until we scrub it.
 *
 * Returns `null` when no token is present in the URL.
 */
export function parseTokenFromUrl(url: { hash?: string; search?: string } | URL | Location): string | null {
  const hash = (url.hash ?? '').replace(/^#/, '');
  const fromHash = pickToken(hash);
  if (fromHash) return fromHash;
  const search = (url.search ?? '').replace(/^\?/, '');
  return pickToken(search);
}

function pickToken(s: string): string | null {
  if (!s) return null;
  const params = new URLSearchParams(s);
  const t = params.get('t') ?? params.get('token');
  if (t && t.length > 0) return t;
  return null;
}

/**
 * Replace the current URL with a clean version (no token, no fragment) using
 * history.replaceState so the token never survives in the address bar,
 * the back/forward stack, or any future Referer header.
 *
 * Safe to call multiple times; we only rewrite if a token-bearing param/hash
 * is present.
 */
export function scrubTokenFromUrl(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  try {
    const loc = window.location;
    const hasHashToken = !!parseTokenFromUrl({ hash: loc.hash, search: '' });
    const hasQueryToken = !!parseTokenFromUrl({ hash: '', search: loc.search });
    if (!hasHashToken && !hasQueryToken) return;
    const cleanedSearch = stripTokenParams(loc.search);
    const cleanedHash = stripTokenParams(loc.hash.replace(/^#/, ''));
    const next =
      loc.pathname +
      (cleanedSearch ? `?${cleanedSearch}` : '') +
      (cleanedHash ? `#${cleanedHash}` : '');
    window.history.replaceState(null, '', next);
  } catch {
    /* ignore */
  }
}

function stripTokenParams(raw: string): string {
  if (!raw) return '';
  const cleaned = raw.replace(/^[?#]/, '');
  if (!cleaned) return '';
  const params = new URLSearchParams(cleaned);
  params.delete('t');
  params.delete('token');
  const out = params.toString();
  return out;
}
